import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import styled from 'styled-components';

import { Button } from '@components/button';
import { FormError } from '@components/form-error';
import useQueryParams from '@hooks/use-query-params';

import useDocumentTitle from 'hooks/useDocumentTitle';
import useFormLoad from 'hooks/useFormLoad';
import usePreviewCheck from 'hooks/usePreviewCheck';
import useScrollToTop from 'hooks/useScrollToTop';
import JobApplication from 'models/JobApplication';
import { useStore } from 'models/stores';
import Separator from 'shared/layout/Separator';
import Spacer from 'shared/layout/Spacer';

import AcceptTerms from './AcceptTerms';
import BackButton from './BackButton';
import FileField from './FileField';
import InputField from './InputField';
import JobHeader, { HeaderGroup } from './JobHeader';
import LanguageDataNotAvailable from './LanguageDataNotAvailable';
import MainHeader from './MainHeader';
import QuestionList from './QuestionList';

function JobApply() {
  const {
    jobStore,
    uiStore: { language, selectJob, t, preview },
    companyStore,
  } = useStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { jobID } = useParams();

  selectJob(jobID);
  let [form, setForm] = useState(null);
  let job = jobStore.items.get(jobID);
  let data = job && job.languages.find((l) => l.language === language);
  let company = companyStore.current.name;
  let backUrl = `/${company}/${language}/${jobID}${location.search}`;
  let referralUuid = useQueryParams('referral_uuid');
  let referredSite = document.referrer.substring(0, 200);
  const frame = useQueryParams('frame');
  usePreviewCheck(jobID);

  useDocumentTitle(data && data.title);

  const formState = job && job.formState;

  useFormLoad(jobID, jobStore, () => {
    navigate(backUrl);
  });

  useScrollToTop();

  useEffect(() => {
    if (job && job.formState === 'loaded' && data) {
      setForm(
        JobApplication.create({
          questions: getSnapshot(data.form),
          language,
          job: getSnapshot(job),
          referralUuid,
          referredSite,
        }),
      );
    }
  }, [job, formState, data, language, referralUuid, referredSite]);

  if (job && job.formState === 'loaded' && !data) {
    return (
      <>
        <MainHeader>
          <BackButton to={backUrl} text={t('backToDetail')} />
        </MainHeader>
        <LanguageDataNotAvailable />
      </>
    );
  }

  if (!job || !form) {
    return null;
  }

  async function handleSubmit(form) {
    const success = await form.apply(frame ? false : true);
    if (frame && success) {
      const data = {
        name: form.name,
        email: form.email,
        type: 'apply_success',
      };

      if ('parentIFrame' in window) {
        window.parentIFrame.sendMessage(data);
      }

      if (window.parent !== window) {
        window.parent.postMessage(data, '*');
      }
    }
  }

  return (
    <Wrapper>
      {!frame && (
        <MainHeader>
          <BackButton to={backUrl} text={t('backToDetail')} disabled={form.disabled} />
        </MainHeader>
      )}
      <ApplyForm>
        {!frame && (
          <>
            <HeaderGroup>
              <JobHeader job={job} showDeadline langData={data} />
            </HeaderGroup>
            <Spacer top={16} bottom={16}>
              <Separator />
            </Spacer>
          </>
        )}
        <InputField form={form} required field="name" />
        <InputField form={form} required field="email" />
        {job.includePhone && <InputField form={form} required={job.requirePhone} field="phone" />}
        {job.includeSsn && <InputField form={form} required={job.requireSsn} field="ssn" />}
        {job.includeSex && <InputField form={form} required={job.requireSex} field="sex" />}
        {job.includeCv && <FileField form={form} required={job.requireCv} field="cv" />}
        {job.includeCoverLetter && (
          <FileField form={form} required={job.requireCoverLetter} field="coverLetter" />
        )}
        <QuestionList form={form} />
        <Spacer top={16} bottom={16}>
          <Separator />
        </Spacer>
        <AcceptTerms form={form} />
        <SubmitWrapper>
          <Button
            text={!form.applying ? t('apply_submit') : t('apply_submitting')}
            onClick={() => handleSubmit(form)}
            isDisabled={preview || form.applying}
          />
          {form.hasErrors && <FormError text={t('apply_form_has_errors')} />}
          {form.nonFieldErrors && <FormError text={form.nonFieldErrors} />}
        </SubmitWrapper>
      </ApplyForm>
    </Wrapper>
  );
}
export default observer(JobApply);

const Wrapper = styled.div`
  margin: 0 auto;
  width: 100%;
  padding: 40px 0;
  max-width: 720px;
  @media (max-width: 720px) {
    padding: 0 40px 40px;
  }

  @media (max-width: 460px) {
    padding: 0 24px 40px;
  }
`;

const ApplyForm = styled.div`
  width: 100%;
  max-width: 448px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SubmitWrapper = styled.div`
  width: 100%;
  max-width: 550px;
  margin: 0;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 480px) {
    button {
      width: 100%;
    }
  }
`;
